import type { ComponentProps, PointerEvent as ReactPointerEvent, ReactNode } from "react";

import { cn } from "@/lib/utils";

export type Corner = "nw" | "ne" | "sw" | "se";

const CORNER_CLASSES: Record<Corner, string> = {
	nw: "-left-1.5 -top-1.5 cursor-nwse-resize",
	ne: "-right-1.5 -top-1.5 cursor-nesw-resize",
	sw: "-left-1.5 -bottom-1.5 cursor-nesw-resize",
	se: "-right-1.5 -bottom-1.5 cursor-nwse-resize",
};

export function ResizeHandles({
	onCornerPointerDown,
	onPointerMove,
	onPointerUp,
}: {
	onCornerPointerDown(corner: Corner): (event: ReactPointerEvent<HTMLSpanElement>) => void;
	onPointerMove(event: ReactPointerEvent<HTMLSpanElement>): void;
	onPointerUp(event: ReactPointerEvent<HTMLSpanElement>): void;
}) {
	return (Object.keys(CORNER_CLASSES) as Corner[]).map((corner) => (
		<span
			key={corner}
			onPointerDown={onCornerPointerDown(corner)}
			onPointerMove={onPointerMove}
			onPointerUp={onPointerUp}
			className={cn(
				"absolute z-10 size-3 rounded-full border border-background bg-primary shadow-card [touch-action:none]",
				CORNER_CLASSES[corner],
			)}
		/>
	));
}

export function ControlButton({
	label,
	active = false,
	guard,
	onClick,
	children,
}: {
	label: string;
	active?: boolean;
	guard: Pick<ComponentProps<"button">, "onPointerDown" | "onMouseDown">;
	onClick(): void;
	children: ReactNode;
}) {
	return (
		<button
			type="button"
			title={label}
			aria-label={label}
			{...guard}
			onClick={onClick}
			className={cn(
				"flex size-7 items-center justify-center rounded-md text-foreground/70 transition-colors hover:bg-foreground/5 hover:text-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring [&_svg]:size-3.5",
				active && "bg-primary/10 text-primary",
			)}
		>
			{children}
		</button>
	);
}
